import StarRating from '../../shared/StarRating'
import GoogleSourceBadge from '../../shared/GoogleSourceBadge'

interface Review {
  id: number | string
  user_name: string
  rating: number
  comment: string
  created_at?: string | null
  source?: string | null
}

interface ReviewsTabProps {
  rating: number
  reviewsCount: number
  reviews: Review[]
}

function isGoogleSource(source?: string | null): boolean {
  if (!source) return false
  const s = source.toLowerCase()
  return s === 'google' || s.includes('google')
}

const ReviewsTab = ({ rating, reviewsCount, reviews }: ReviewsTabProps) => {
  const googleCount = reviews.filter((r) => isGoogleSource(r.source)).length

  return (
    <div className="animate-fade-in-up">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 sm:mb-6 p-4 rounded-2xl bg-gradient-to-r from-amber-50 to-primary-50 border border-primary-100">
        <div>
          <h3 className="text-lg sm:text-xl font-semibold text-gray-900">Guest reviews</h3>
          <p className="text-xs sm:text-sm text-gray-600 mt-1">
            {googleCount > 0
              ? `${googleCount} review${googleCount === 1 ? '' : 's'} from Google · verified guest feedback`
              : 'Verified feedback from previous guests'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <StarRating rating={rating} size="md" showValue />
          {reviewsCount > 0 && (
            <span className="text-xs sm:text-sm font-medium text-gray-600">({reviewsCount})</span>
          )}
        </div>
      </div>

      {reviews.length > 0 ? (
        <div className="space-y-3 sm:space-y-4">
          {reviews.map((review) => (
            <article key={review.id} className="bg-white border border-gray-100 rounded-xl sm:rounded-2xl p-4 sm:p-5 shadow-sm">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 bg-primary-500 rounded-full flex items-center justify-center text-white font-semibold text-sm shrink-0">
                    {(review.user_name || 'G').charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h4 className="font-semibold text-gray-900 text-sm sm:text-base truncate">{review.user_name}</h4>
                      {isGoogleSource(review.source) && <GoogleSourceBadge />}
                    </div>
                    <StarRating rating={review.rating} size="sm" />
                  </div>
                </div>
                <time className="text-[10px] sm:text-xs text-gray-500 shrink-0">
                  {review.created_at ? new Date(review.created_at).toLocaleDateString() : 'Recent'}
                </time>
              </div>
              <p className="text-gray-700 text-sm sm:text-base leading-relaxed whitespace-pre-line">{review.comment}</p>
            </article>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 rounded-2xl bg-gray-50 border border-dashed border-gray-200">
          <h4 className="text-base font-semibold text-gray-900 mb-2">No reviews yet</h4>
          <p className="text-gray-600 text-sm">Be the first to stay and share your experience.</p>
        </div>
      )}
    </div>
  )
}

export default ReviewsTab
